/**
 * InsightCampaignsLauncher
 *
 * P_SURVEY_INTELLIGENCE_ENGINE_1.0 — Slice A
 *
 * The campaign-launcher surface that lives at the top of the Surveys
 * home. Each tile is an outcome-named "business initiative" the
 * operator can launch — never a "type of survey". Clicking a tile
 * opens the CampaignDetailDialog where the operator reads the
 * five-facet "explain itself" block, then launches the underlying
 * Guided Survey recipe via the existing wizard.
 *
 * Single-engine guarantee
 * -----------------------
 * The launcher itself only fetches `/api/surveys/campaigns`. The
 * actual survey creation goes through `POST /api/surveys/guided/
 * instantiate` exactly like a normal Guided Survey — no parallel
 * runtime, no parallel response store, no parallel analytics.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, ChevronRight, BookOpen } from 'lucide-react';

import { Card, CardContent } from '../../../components/ui/card';
import { surveysAPI } from '../api';
import CampaignDetailDialog from './CampaignDetailDialog';
import GuidedSurveyWizard from '../guided/GuidedSurveyWizard';

const InsightCampaignsLauncher = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'en';
    const tr = (bag) => (bag && (bag[lang] || bag.en)) || '';

    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [presetOutcomeId, setPresetOutcomeId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        surveysAPI.campaigns.list()
            .then((d) => {
                if (cancelled) return;
                setCampaigns(d.campaigns || []);
            })
            .catch(() => { if (!cancelled) setCampaigns([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const openDetail = (id) => {
        setSelectedId(id);
        setDetailOpen(true);
    };

    const launchFromCampaign = (recipeId) => {
        setDetailOpen(false);
        setPresetOutcomeId(recipeId);
        // Open the wizard on the next tick so the preset effect fires
        // cleanly after the dialog state settles.
        setTimeout(() => setWizardOpen(true), 50);
    };

    if (loading) {
        return (
            <Card data-testid="insight-campaigns-loading">
                <CardContent className="pt-6 pb-6">
                    <div className="h-4 w-1/3 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                    <div className="mt-3 h-3 w-1/2 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                </CardContent>
            </Card>
        );
    }

    if (!campaigns.length) return null;

    return (
        <section
            className="space-y-3"
            data-testid="insight-campaigns-launcher"
            aria-labelledby="insight-campaigns-heading"
        >
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2
                        id="insight-campaigns-heading"
                        className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2"
                    >
                        <Sparkles className="w-4 h-4 text-fuchsia-500" />
                        {t('surveys.campaigns.heading', 'Launch a business initiative')}
                    </h2>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mt-0.5 max-w-2xl">
                        {t(
                            'surveys.campaigns.subheading',
                            'Pick an initiative below — the platform builds the survey, explains the metric, and tells you what to do with the answers.',
                        )}
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {campaigns.map((c) => (
                    <button
                        key={c.id}
                        type="button"
                        onClick={() => openDetail(c.id)}
                        data-testid={`campaign-tile-${c.id}`}
                        data-insight-id="surveys.campaigns.tile"
                        data-insight-label="Insight Campaign tile"
                        className="group text-left rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-4 transition-all hover:shadow-md hover:-translate-y-0.5 hover:border-fuchsia-300 dark:hover:border-fuchsia-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400"
                    >
                        <div className="flex items-start gap-3">
                            <div className="text-2xl leading-none mt-0.5" aria-hidden="true">{c.icon}</div>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                                    {tr(c.title)}
                                </div>
                                <div className="text-xs text-slate-600 dark:text-slate-300 mt-1 line-clamp-3">
                                    {tr(c.tagline)}
                                </div>
                                <div className="text-[11px] text-fuchsia-600 dark:text-fuchsia-300 mt-2 inline-flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                                    <BookOpen className="w-3 h-3" />
                                    {t('surveys.campaigns.learnMore', 'Learn more & launch')}
                                    <ChevronRight className="w-3 h-3" />
                                </div>
                            </div>
                        </div>
                    </button>
                ))}
            </div>

            <CampaignDetailDialog
                open={detailOpen}
                onOpenChange={setDetailOpen}
                campaignId={selectedId}
                onLaunch={launchFromCampaign}
            />

            <GuidedSurveyWizard
                open={wizardOpen}
                onOpenChange={(open) => {
                    setWizardOpen(open);
                    if (!open) setPresetOutcomeId(null);
                }}
                presetOutcomeId={presetOutcomeId}
            />
        </section>
    );
};

export default InsightCampaignsLauncher;
